import React, { useState } from "react";
import HeadingOne from "../../components/HeadingOne";
import { ExistingSiteKey } from "../../models/site-key";

// Define tab names as an enum or const array for better management
export enum ReconciliationTabType {
  ALL_PAYMENTS = "All Payments",
  CREDIT_CARD = "Credit Card",
  CASH = "Cash",
  CHECKS = "Checks",
  ACH = "ACH/EFT",
}

interface Props {
  siteKey: string;
  quickbooksEnabled: boolean;
  children: {
    allPaymentsSlot: React.ReactNode;
    creditCardSlot: React.ReactNode;
    cashSlot: React.ReactNode;
    checksSlot: React.ReactNode;
    achSlot: React.ReactNode;
  };
}

// Mapping tab types to content slots
const tabContentMap = (props: Props) => ({
  [ReconciliationTabType.ALL_PAYMENTS]: props.children.allPaymentsSlot,
  [ReconciliationTabType.CREDIT_CARD]: props.children.creditCardSlot,
  [ReconciliationTabType.CASH]: props.children.cashSlot,
  [ReconciliationTabType.CHECKS]: props.children.checksSlot,
  [ReconciliationTabType.ACH]: props.children.achSlot,
});

export default function ReconciliationPage(props: Props): JSX.Element {
  // State to manage the active tab, default to All Payments
  const [activeTab, setActiveTab] = useState<ReconciliationTabType>(
    ReconciliationTabType.ALL_PAYMENTS,
  );

  const tabs = Object.values(ReconciliationTabType);
  const content = tabContentMap(props)[activeTab];

  return (
    <div className="space-y-8">
      <HeadingOne>Payment Reconciliation</HeadingOne>
      <div className="mb-2">
        <p className="text-gray-700">
          Use this tool to confirm that payments recorded in Stilt match amounts
          received via bank deposit. Select a tab below to begin reconciling for
          that payment method.
        </p>

        {props.quickbooksEnabled === true && (
          <div className="mt-2 flex flex-row items-center gap-2 rounded-md bg-gray-100 p-2">
            <img
              src="/quickbooks-2.svg"
              alt="QuickBooks"
              style={{ height: "24px" }}
            />
            <p className="text-gray-700">
              When you create a bank deposit, it will automatically push to
              QuickBooks. This will clear those payments from the undeposited
              funds account in Quickbooks.
            </p>
          </div>
        )}
      </div>

      {/* Tab Buttons - Styled like TableTabs */}
      <div className="mb-4 grid max-w-fit grid-cols-3 gap-2 md:flex md:flex-row md:gap-1 md:rounded-xl md:bg-gray-100">
        {tabs.map((tabName) => (
          <button
            key={tabName}
            onClick={() => setActiveTab(tabName)}
            className={`block w-32 rounded p-3 text-base font-medium ring-white/60 ring-offset-2 ring-offset-primaryLight focus:outline-none focus:ring-2 ${
              activeTab === tabName
                ? "bg-primary text-primaryButtonText"
                : "bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200/40 hover:text-primary"
            }`}
          >
            {tabName}
          </button>
        ))}
      </div>

      {/* Tab Content Area */}
      <div className="mt-2 rounded-xl bg-white p-3 shadow ring-1 ring-black ring-opacity-5">
        {content}
      </div>
    </div>
  );
}
